import { mapValues } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAsyncExport } from 'api/async-export/use-async-export';
import { ProjectId } from 'api/models';
import { Action, EventType, ObjectType, SocketData } from 'api/pushes';
import { TaskAPI } from 'api/tasks';
import { RootState } from 'data';
import { Task as NormalizedTask, taskNormalizer } from 'data/tasks/schema';
import { useProjectWSUpdates } from 'hooks/use-project-ws-updates';
import { useUserWsUpdates } from 'hooks/use-user-ws-updates';
import { TaskField } from 'stores/fields';
import { created, deleted, Task, updated } from 'stores/tasks';
import { withValidCustomFieldsForData } from 'utils/duplicate-fields-transformer';
import { toStoreTask } from 'utils/to-store-task';
import { unreachable } from 'utils/unreachable';
import { duplicateTasksToStore } from './duplicate-tasks-to-store';

type TaskSocketData = Pick<Task, 'taskId'>;

export const useProjectTasksSocketUpdates = (
  [, project]: ProjectId,
  onExportCompleted: () => void = () => {},
  isExportInProgress = false,
): void => {
  const dispatch = useDispatch();
  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const fields_lookup = useMemo(
    () =>
      mapValues(fieldsStore.byId, ([, x]) => x!) as Record<number, TaskField>,
    [fieldsStore.byId],
  );

  const tasksStore = useSelector(({ tasks_v2 }: RootState) => tasks_v2);

  const tasks_lookup = useMemo(() => {
    return mapValues(tasksStore.byId, ([, x]) => x!) as Record<number, Task>;
  }, [tasksStore.byId]);

  const to_row = useCallback(
    (task: NormalizedTask) => {
      return toStoreTask(fields_lookup, tasks_lookup)(task);
    },
    [fields_lookup, tasks_lookup],
  );
  const handler = useCallback(
    async ({
      data,
      action,
      requestId,
      projectId,
    }: SocketData<TaskSocketData | number[]>) => {
      if (Array.isArray(data)) {
        duplicateTasksToStore({
          projectId,
          data,
          requestId,
          dispatch,
          to_row,
          shouldUpdateTasksInV1Store: false,
        });

        return;
      }
      switch (action) {
        case Action.Create: {
          const task = await TaskAPI.getTask(projectId, data.taskId);
          const updatedTask = {
            ...task,
            fields: task.fields.map(withValidCustomFieldsForData),
          };
          const {
            entities: {
              tasks: { [data.taskId]: normalized },
            },
          } = taskNormalizer(updatedTask);

          dispatch(created([to_row(normalized), requestId]));

          return;
        }
        case Action.Update: {
          const task = await TaskAPI.getTask(projectId, data.taskId);
          const updatedTask = {
            ...task,
            fields: task.fields.map(withValidCustomFieldsForData),
          };
          const {
            entities: {
              tasks: { [data.taskId]: normalized },
            },
          } = taskNormalizer(updatedTask);

          dispatch(updated([to_row(normalized)]));

          return;
        }
        case Action.Delete:
          dispatch(deleted([{ taskId: data.taskId }]));

          return;
        default:
          unreachable(action as never);
      }
    },
    [dispatch, to_row],
  );

  const { handleExport } = useAsyncExport({
    onExportCompleted,
    isExportInProgress,
  });

  useProjectWSUpdates(
    project,
    handler,
    EventType(Action.Create, ObjectType.Task),
  );
  useProjectWSUpdates(
    project,
    handler,
    EventType(Action.Update, ObjectType.Task),
  );
  useProjectWSUpdates(
    project,
    handler,
    EventType(Action.Delete, ObjectType.Task),
  );
  useProjectWSUpdates(
    project,
    handler,
    EventType(Action.Duplicate, ObjectType.Task),
  );

  useUserWsUpdates(handleExport, [Action.Create, ObjectType.AsyncProcessor]);
};
